/**
 * Table Store
 * 管理台桌信息、配置、历史记录等
 */

import { defineStore } from 'pinia'
import type { TableInfo, Notice, TableConfigData } from '@/types/websocket'
import { GameType } from '@/types/enums'
import {
  generateRoadmap,
  type RoadmapData,
  handleListString,
  calculateWinCounts,
  type BaccaratStats,
  type DragonTigerStats,
} from '@/utils/roadmap'
import {
  processSideGameMainRoad,
  calculateSideGameStats,
  type SideGameRoadData,
  type SideGameType,
  type SideGameStats,
} from '@/utils/side-game-roadmap'
import {
  processGanZiBaoMainRoad,
  calculateGanZiBaoStats,
  type GanZiBaoRoadData,
  type GanZiBaoStats,
} from '@/utils/ganzibao-roadmap'
import { useUserStore } from './user'
import type { TableBetData, BetRecord } from '@/types/bet'
import { normalizeUUID } from '@/utils/string'
import { useSettingsStore } from './settings'

/**
 * 将总金额拆分为用户配置的筹码面值列表
 *
 * 使用贪心算法，优先使用大面值筹码
 * 例如: chipList=[10,50,100], 150 → [100, 50]
 *       chipList=[10,20,100], 150 → [100, 20, 20, 10]
 *
 * @param totalAmount - 需要拆分的总金额
 * @param chipList - 用户配置的筹码列表 (从小到大排列)
 * @returns 筹码面值数组
 */
function splitAmountIntoChips(totalAmount: number, chipList: number[]): number[] {
  const chips: number[] = []
  let remaining = totalAmount

  // 从大到小遍历筹码面值
  const sortedChips = [...chipList].sort((a, b) => b - a)

  for (const chipValue of sortedChips) {
    while (remaining >= chipValue) {
      chips.push(chipValue)
      remaining -= chipValue
    }
  }

  return chips
}

interface TableState {
  tableList: TableInfo[] // 台桌列表
  noticeList: Notice[] // 公告列表
  companyPhoneNum: string // 公司电话
  currentCompanyType: string // 当前公司类型 (从旧项目迁移)
  /** 台桌特定配置数据 (USLO06) - 包含 curShoesData 路单原始数据等台桌特有信息 */
  // tableConfigs: Record<string, TableConfigData> // key: tableId
  /** 全局通用配置 (USLO06) - 赔率等所有台桌共享的配置 */
  globalConfig: TableConfigData | null
  /** 倒计时定时器ID */
  countdownTimerId: number | null
  /** 是否已接收过 USLO06 (整个会话中只接收一次) */
  hasReceivedUSLO06: boolean
  /** 所有台桌的下注数据 */
  tableBets: Record<string, TableBetData> // key: tableId
}

export const useTableStore = defineStore('table', {
  state: (): TableState => ({
    tableList: [],
    noticeList: [],
    companyPhoneNum: '',
    currentCompanyType: '',
    // tableConfigs: {},
    globalConfig: null,
    countdownTimerId: null,
    hasReceivedUSLO06: false,
    tableBets: {},
  }),

  getters: {
    /**
     * 是否有台桌
     */
    hasTables: state => state.tableList.length > 0,

    /**
     * 获取百家乐游戏台桌 (TAN - 普通百家乐)
     *
     * 直接从 tableList 过滤，不再依赖 gamedeskList
     * 这样 USLO25 动态添加/显示的台桌也能正确显示
     * 按 tableNo 排序，保持台桌顺序稳定
     */
    bjlTables: state => {
      return state.tableList
        .filter(
          table =>
            table.deskIsVisiable !== '1' &&
            table.gameType === 'BJL' &&
            table.deskType === 'TAN' &&
            (state.currentCompanyType === '' || table.companyType === state.currentCompanyType)
        )
        .sort((a, b) => a.tableNo.localeCompare(b.tableNo))
    },

    /**
     * 获取极速百家乐台桌 (NET - 网络百家乐/极速百家乐)
     *
     * 直接从 tableList 过滤，不再依赖 gamedeskList
     * 按 tableNo 排序，保持台桌顺序稳定
     */
    quickBjlTables: state => {
      return state.tableList
        .filter(
          table =>
            table.deskIsVisiable !== '1' &&
            table.gameType === 'BJL' &&
            table.deskType === 'NET' &&
            (state.currentCompanyType === '' || table.companyType === state.currentCompanyType)
        )
        .sort((a, b) => a.tableNo.localeCompare(b.tableNo))
    },

    /**
     * 根据游戏类型获取台桌
     *
     * 直接从 tableList 过滤，不再依赖 gamedeskList
     * 按 tableNo 排序，保持台桌顺序稳定
     *
     * @param gameType 游戏类型 (如: 'LHD', 'SNN', 'ZJH', 'GZB' 等)
     */
    getTablesByGameType: state => (gameType: string) => {
      return state.tableList
        .filter(
          table =>
            table.deskIsVisiable !== '1' &&
            table.gameType === gameType &&
            (state.currentCompanyType === '' || table.companyType === state.currentCompanyType)
        )
        .sort((a, b) => a.tableNo.localeCompare(b.tableNo))
    },

    /**
     * 获取指定台桌的路单数据
     *
     * 支持的游戏类型:
     * - BJL: 百家乐
     * - LHD: 龙虎斗
     *
     * @param tableId 台桌ID
     * @returns 路单数据 (如果台桌不存在或游戏类型不支持,返回 null)
     */
    getRoadmapData:
      state =>
      (tableId: string): RoadmapData | null => {
        // 获取台桌信息
        const tableInfo = state.tableList.find(t => t.tableId === tableId)
        if (!tableInfo) {
          return null
        }

        // 检查是否有路单数据
        if (!tableInfo.curShoesData || tableInfo.curShoesData.length === 0) {
          return null
        }

        // 只支持百家乐和龙虎斗
        if (tableInfo.gameType !== 'BJL' && tableInfo.gameType !== 'LHD') {
          return null
        }

        // 映射游戏类型到 roadmap 的格式
        const gameType = tableInfo.gameType === 'BJL' ? 'BJL' : 'LHD'

        // 生成路单数据 (使用 tableInfo.curShoesData)
        return generateRoadmap(tableInfo.curShoesData, gameType)
      },

    /**
     * 获取所有百家乐台桌的路单数据
     */
    bjlRoadmaps: state => {
      const roadmaps: Record<string, RoadmapData> = {}

      state.tableList
        .filter(t => t.gameType === 'BJL')
        .forEach(table => {
          if (table.curShoesData && table.curShoesData.length > 0) {
            roadmaps[table.tableId] = generateRoadmap(table.curShoesData, 'BJL')
          }
        })

      return roadmaps
    },

    /**
     * 获取所有龙虎台桌的路单数据
     */
    lhdRoadmaps: state => {
      const roadmaps: Record<string, RoadmapData> = {}

      state.tableList
        .filter(t => t.gameType === 'LHD')
        .forEach(table => {
          if (table.curShoesData && table.curShoesData.length > 0) {
            roadmaps[table.tableId] = generateRoadmap(table.curShoesData, 'LHD')
          }
        })

      return roadmaps
    },

    /**
     * 获取指定台桌的统计数据
     *
     * 支持的游戏类型:
     * - BJL: 百家乐
     * - LHD: 龙虎斗
     * - SNN: 牛牛
     * - ZJH: 炸金花
     * - GZB: 杆子宝
     *
     * @param tableId 台桌ID
     * @returns 统计数据
     */
    getTableStats:
      state =>
      (tableId: string): BaccaratStats | DragonTigerStats | SideGameStats | GanZiBaoStats | null => {
        // 获取台桌信息
        const tableInfo = state.tableList.find(t => t.tableId === tableId)
        if (!tableInfo) {
          return null
        }

        // 检查是否有路单数据
        if (!tableInfo.curShoesData || tableInfo.curShoesData.length === 0) {
          return null
        }

        // 根据游戏类型计算统计数据
        switch (tableInfo.gameType) {
          case 'BJL': {
            // 百家乐统计
            const parsedData = handleListString(tableInfo.curShoesData, 'BJL')
            return calculateWinCounts(parsedData, 'BJL')
          }
          case 'LHD': {
            // 龙虎斗统计
            const parsedData = handleListString(tableInfo.curShoesData, 'LHD')
            return calculateWinCounts(parsedData, 'LHD')
          }
          case GameType.SNN:
          case GameType.ZJH: {
            // 牛牛/炸金花统计
            return calculateSideGameStats(tableInfo.curShoesData)
          }
          case GameType.GZB: {
            // 杆子宝统计
            return calculateGanZiBaoStats(tableInfo.curShoesData)
          }
          default:
            return null
        }
      },

    /**
     * 获取指定台桌的边注游戏路单数据 (牛牛/炸金花)
     *
     * @param tableId 台桌ID
     * @returns 边注游戏路单数据
     */
    getSideGameRoadData:
      state =>
      (tableId: string): SideGameRoadData | null => {
        // 获取台桌信息
        const tableInfo = state.tableList.find(t => t.tableId === tableId)
        if (!tableInfo) {
          return null
        }

        // 检查是否有路单数据
        if (!tableInfo.curShoesData || tableInfo.curShoesData.length === 0) {
          return null
        }

        // 只支持牛牛和炸金花
        if (tableInfo.gameType !== GameType.SNN && tableInfo.gameType !== GameType.ZJH) {
          return null
        }

        // 根据游戏类型处理路单数据
        const gameType = tableInfo.gameType as SideGameType
        return processSideGameMainRoad(tableInfo.curShoesData, gameType, 30)
      },

    /**
     * 获取指定台桌的杆子宝路单数据
     *
     * @param tableId 台桌ID
     * @returns 杆子宝路单数据
     */
    getGanZiBaoRoadData:
      state =>
      (tableId: string): GanZiBaoRoadData | null => {
        // 获取台桌信息
        const tableInfo = state.tableList.find(t => t.tableId === tableId)
        if (!tableInfo) {
          return null
        }

        // 检查是否有路单数据
        if (!tableInfo.curShoesData || tableInfo.curShoesData.length === 0) {
          return null
        }

        // 只支持杆子宝
        if (tableInfo.gameType !== GameType.GZB) {
          return null
        }

        // 处理杆子宝路单数据
        return processGanZiBaoMainRoad(tableInfo.curShoesData, 30)
      },
  },

  actions: {
    /**
     * 设置台桌列表
     */
    setTableList(tables: TableInfo[]) {
      this.tableList = tables

      // 启动倒计时定时器 (如果还没启动)
      if (!this.countdownTimerId) {
        this.startCountdownTimer()
      }
    },

    /**
     * 启动倒计时定时器
     *
     * 每秒减少所有台桌的 countdown 值,并更新游戏状态
     *
     * 说明:
     * - 正常游戏流程 (局数 > 有效结果数) 时,倒计时变化不会触发 GRUN27
     * - 只有在数据异常时 (局数 < 有效结果数等),才会发送 GRUN27 请求同步
     * - 因此在定时器中调用 getGameState 是安全的,不会造成消息轰炸
     */
    startCountdownTimer() {
      // 先清除已有的定时器 (防止重复启动)
      this.stopCountdownTimer()

      this.countdownTimerId = window.setInterval(async () => {
        // 动态导入,避免循环依赖
        const { getGameState } = await import('@/utils/updateTableGameStatus')
        const { normalizeUUID } = await import('@/utils/string')

        this.tableList.forEach(table => {
          // 倒计时减 1 (如果大于 0)
          if (table.countdown > 0) {
            table.countdown--

            // 倒计时变为 0 时,清除该台桌的临时下注
            if (table.countdown === 0) {
              this.clearPendingBets(normalizeUUID(table.tableId))
            }
          }

          // 重新计算游戏状态 (倒计时变化可能导致状态变化)
          const validResultCount = table.curShoesData?.filter(item => item && item !== '' && item !== '0').length || 0

          const gameState = getGameState(table.shoeNumber, table.roundNumber, validResultCount, table.countdown)

          // 更新游戏状态
          table.gameStatus = gameState.id
        })
      }, 1000)
    },

    /**
     * 停止倒计时定时器
     */
    stopCountdownTimer() {
      if (this.countdownTimerId !== null) {
        clearInterval(this.countdownTimerId)
        this.countdownTimerId = null
      }
    },

    /**
     * 设置公告列表
     */
    setNoticeList(notices: Notice[]) {
      this.noticeList = notices
    },

    /**
     * 设置公司电话
     */
    setCompanyPhoneNum(phoneNum: string) {
      this.companyPhoneNum = phoneNum
    },

    /**
     * 更新当前公司类型
     *
     * 从旧项目 userInfo.js 的 updateCurrentCompanyType() 迁移而来
     */
    updateCurrentCompanyType(companyType: string) {
      this.currentCompanyType = companyType
    },

    /**
     * 设置台桌配置 (USLO06 数据)
     *
     * @param tableId 台桌ID
     * @param config 台桌配置数据 (包含 curShoesData 路单原始数据)
     */
    // setTableConfig(tableId: string, config: TableConfigData) {
    //   this.tableConfigs[tableId] = config
    // },

    /**
     * 标记已接收 USLO06 (发送过 USLO04 并成功接收 USLO06)
     */
    markUSLO06Received() {
      this.hasReceivedUSLO06 = true
    },

    /**
     * 从 USLO06 数据更新 TableInfo (重合字段)
     *
     * @param tableId 台桌ID
     * @param uslo06Data USLO06 完整数据
     */
    updateTableInfoFromUSLO06(tableId: string, uslo06Data: TableConfigData) {
      const table = this.tableList.find(t => t.tableId === tableId)
      if (table) {
        // 更新重合字段
        table.curShoesData = uslo06Data.curShoesData
        table.bettingLimits = uslo06Data.bettingLimits
        table.shoeNumber = uslo06Data.shoeNumber
        table.roundNumber = uslo06Data.roundNumber
        table.countdown = uslo06Data.countdown
      }
    },

    /**
     * 从 USLO25 数据更新 TableInfo (实时台桌数据更新)
     *
     * USLO25 是实时台桌配置变更消息,包含:
     * - 基础配置 (deskName, deskIsVisiable, deskPrivate, deskVipList, gameType, deskType, companyType)
     * - 台桌游戏信息 (tableId, tableNo, vipNotice, shoeNumber, roundNumber, gameStatus, countdown, curShoesData)
     * - 实时彩池 (betPotData)
     * - 投注限额 (bettingLimits)
     * - 发牌信息 (curSendCardCount, cardList)
     *
     * @param uslo25Data USLO25 完整数据
     */
    updateTableFromUSLO25(uslo25Data: import('@/services/websocket/parsers/USLO25').USLO25Data) {
      const table = this.tableList.find(t => t.tableNo === uslo25Data.tableNo)

      if (table) {
        // 更新基础配置
        table.deskName = uslo25Data.deskName
        table.deskIsVisiable = uslo25Data.deskIsVisiable
        table.deskPrivate = uslo25Data.deskPrivate
        table.deskVipList = uslo25Data.deskVipList
        table.deskType = uslo25Data.deskType
        table.companyType = uslo25Data.companyType

        // 更新台桌游戏信息
        table.tableNo = uslo25Data.tableNo
        table.vipNotice = uslo25Data.vipNotice
        table.shoeNumber = uslo25Data.shoeNumber
        table.roundNumber = uslo25Data.roundNumber
        table.gameStatus = uslo25Data.gameStatus
        table.countdown = uslo25Data.countdown
        table.curShoesData = uslo25Data.curShoesData

        // 更新实时彩池数据
        table.betPotData = uslo25Data.betPotData

        // 更新投注限额
        table.bettingLimits = uslo25Data.bettingLimits

        // 更新发牌信息
        table.curSendCardCount = uslo25Data.curSendCardCount
        table.cardList = uslo25Data.cardList
      } else {
        // 台桌不存在 (可能是一开始隐藏的台桌，现在变为可见)
        // 从 userStore 的 channelList 和 gamedeskList 中获取额外信息并添加新台桌
        const userStore = useUserStore()

        // 从 channelList 获取视频流信息
        const channelInfo = userStore.channelList?.find(ch => ch.tableNo === uslo25Data.tableNo)
        // 从 gamedeskList 获取游戏桌信息
        const gamedeskInfo = userStore.gamedeskList?.find(desk => desk.tableNo === uslo25Data.tableNo)

        // 构建新台桌对象
        // 注意: 合并顺序很重要! 先合并 USLO02 的基础数据，再用 USLO25 的实时数据覆盖
        // 这样 USLO25 的最新字段 (如 gameStatus, countdown 等) 不会被旧数据覆盖
        const newTable: TableInfo = {
          // 1. 先合并 channelList 数据 (视频流信息)
          ...(channelInfo || {}),
          // 2. 再合并 gamedeskList 数据 (游戏桌基础信息)
          ...(gamedeskInfo || {}),
          // 3. 最后用 USLO25 实时数据覆盖 (确保最新状态)
          tableId: uslo25Data.tableId,
          tableNo: uslo25Data.tableNo,
          deskName: uslo25Data.deskName,
          deskIsVisiable: uslo25Data.deskIsVisiable,
          deskPrivate: uslo25Data.deskPrivate,
          deskVipList: uslo25Data.deskVipList,
          deskType: uslo25Data.deskType,
          companyType: uslo25Data.companyType,
          vipNotice: uslo25Data.vipNotice,
          shoeNumber: uslo25Data.shoeNumber,
          roundNumber: uslo25Data.roundNumber,
          gameStatus: uslo25Data.gameStatus,
          countdown: uslo25Data.countdown,
          curShoesData: uslo25Data.curShoesData,
          betPotData: uslo25Data.betPotData,
          bettingLimits: uslo25Data.bettingLimits,
          curSendCardCount: uslo25Data.curSendCardCount,
          cardList: uslo25Data.cardList,
          // TableInfo 必需字段 (USLO25 包含 gameType)
          gameType: (uslo25Data.gameType as GameType) || GameType.BJL,
          playerCount: 0,
          betDataList: [],
        }

        // 添加到台桌列表
        this.tableList.push(newTable)

        console.log('[USLO25] 添加新台桌:', {
          tableNo: uslo25Data.tableNo,
          deskIsVisiable: uslo25Data.deskIsVisiable,
        })
      }
    },

    /**
     * 更新台桌配置的部分数据
     *
     * @param tableId 台桌ID
     * @param updates 要更新的配置项
     */
    // updateTableConfig(tableId: string, updates: Partial<TableConfigData>) {
    //   if (this.tableConfigs[tableId]) {
    //     this.tableConfigs[tableId] = {
    //       ...this.tableConfigs[tableId],
    //       ...updates,
    //     }
    //   }
    // },

    /**
     * 清空台桌数据 (退出登录时调用)
     */
    clear() {
      // 停止倒计时定时器
      this.stopCountdownTimer()

      this.tableList = []
      this.noticeList = []
      this.companyPhoneNum = ''
      this.currentCompanyType = ''
      // this.tableConfigs = {}
      this.globalConfig = null
      this.hasReceivedUSLO06 = false
      this.tableBets = {}
    },

    // ==================== 下注数据管理 ====================

    /**
     * 初始化台桌下注数据 (如果不存在)
     * 注意: tableId 统一转为小写以保持一致性
     */
    initTableBetData(tableId: string) {
      const normalizedId = normalizeUUID(tableId)
      if (!this.tableBets[normalizedId]) {
        this.tableBets[normalizedId] = {
          tableId: normalizedId,
          pendingBets: [],
          confirmedBets: [],
        }
      }
    },

    /**
     * 添加临时下注 (用户点击下注区域)
     */
    addPendingBet(tableId: string, bet: BetRecord) {
      const normalizedId = normalizeUUID(tableId)
      this.initTableBetData(normalizedId)
      const betData = this.tableBets[normalizedId]
      if (betData) {
        betData.pendingBets.push(bet)
      }
    },

    /**
     * 确认所有临时下注 (用户点击"确认"按钮)
     * 将 pendingBets 移动到 confirmedBets
     */
    confirmPendingBets(tableId: string) {
      const normalizedId = normalizeUUID(tableId)
      this.initTableBetData(normalizedId)
      const betData = this.tableBets[normalizedId]

      if (betData) {
        // 将临时下注移动到已确认下注
        betData.confirmedBets.push(...betData.pendingBets)
        betData.pendingBets = []
      }
    },

    /**
     * 根据后端返回的投注数据同步已确认下注
     * 解决问题: 最后一秒投注时,游戏状态变化会清空 pendingBets,
     * 但后端返回投注成功时 pendingBets 已经是空的,导致筹码丢失
     *
     * @param tableId - 台桌ID
     * @param tableInfo - 后端返回的投注信息 (包含各区域的投注金额)
     * @param gameType - 游戏类型 (用于区域映射)
     */
    syncConfirmedBetsFromServer(
      tableId: string,
      tableInfo: {
        betBanker: string
        betPlayer: string
        betTie: string
        betBankerPair: string
        betPlayerPair: string
        bets?: string[]
      },
      gameType: string
    ) {
      const normalizedId = normalizeUUID(tableId)
      this.initTableBetData(normalizedId)
      const betData = this.tableBets[normalizedId]

      if (!betData) return

      // 清空现有的已确认下注和临时下注
      betData.confirmedBets = []
      betData.pendingBets = []

      // 根据游戏类型解析后端返回的投注数据
      const timestamp = Date.now()

      // 获取用户配置的筹码列表
      const settingsStore = useSettingsStore()
      const userChipList = settingsStore.chipList

      // 辅助函数: 将区域的总金额拆分成多个筹码记录
      const addChipsForZone = (zone: string, totalAmount: number, startIndex: number): number => {
        if (totalAmount <= 0) return startIndex

        const chips = splitAmountIntoChips(totalAmount, userChipList)
        chips.forEach((chipAmount, chipIndex) => {
          betData.confirmedBets.push({
            betId: `server_${timestamp}_${startIndex}_${chipIndex}`,
            zone,
            amount: chipAmount,
          })
        })

        return startIndex + 1
      }

      if (gameType === 'BJL' || gameType === 'JSBJL') {
        // 百家乐/极速百家乐: zone1=闲对, zone2=闲, zone3=和, zone4=庄, zone5=庄对
        const zoneMappings = [
          { zone: 'zone4', amount: Number(tableInfo.betBanker) || 0 }, // 庄
          { zone: 'zone2', amount: Number(tableInfo.betPlayer) || 0 }, // 闲
          { zone: 'zone3', amount: Number(tableInfo.betTie) || 0 }, // 和
          { zone: 'zone5', amount: Number(tableInfo.betBankerPair) || 0 }, // 庄对
          { zone: 'zone1', amount: Number(tableInfo.betPlayerPair) || 0 }, // 闲对
        ]

        let index = 0
        zoneMappings.forEach(({ zone, amount }) => {
          index = addChipsForZone(zone, amount, index)
        })
      } else if (gameType === 'LHD') {
        // 龙虎斗: zone1=龙, zone3=和, zone5=虎
        const zoneMappings = [
          { zone: 'zone1', amount: Number(tableInfo.betBanker) || 0 }, // 龙
          { zone: 'zone5', amount: Number(tableInfo.betPlayer) || 0 }, // 虎
          { zone: 'zone3', amount: Number(tableInfo.betTie) || 0 }, // 和
        ]

        let index = 0
        zoneMappings.forEach(({ zone, amount }) => {
          index = addChipsForZone(zone, amount, index)
        })
      } else if (gameType === 'GZB') {
        // 干子宝: zone1=干, zone2=通, zone4=1对, zone5=花对
        const zoneMappings = [
          { zone: 'zone1', amount: Number(tableInfo.betBanker) || 0 }, // 干
          { zone: 'zone2', amount: Number(tableInfo.betPlayer) || 0 }, // 通
          { zone: 'zone4', amount: Number(tableInfo.betTie) || 0 }, // 1对
          { zone: 'zone5', amount: Number(tableInfo.betBankerPair) || 0 }, // 花对
        ]

        let index = 0
        zoneMappings.forEach(({ zone, amount }) => {
          index = addChipsForZone(zone, amount, index)
        })
      } else if (gameType === 'SNN' || gameType === 'ZJH') {
        // 牛牛/炸金花 (副游戏)
        // p1_equal → betBanker, p1_double → betPlayer
        // p2_equal → betTie, p2_double → betBankerPair
        // p3_equal → betPlayerPair, p3_double → bets[0]
        const zoneMappings = [
          { zone: 'p1_equal', amount: Number(tableInfo.betBanker) || 0 },
          { zone: 'p1_double', amount: Number(tableInfo.betPlayer) || 0 },
          { zone: 'p2_equal', amount: Number(tableInfo.betTie) || 0 },
          { zone: 'p2_double', amount: Number(tableInfo.betBankerPair) || 0 },
          { zone: 'p3_equal', amount: Number(tableInfo.betPlayerPair) || 0 },
          { zone: 'p3_double', amount: Number(tableInfo.bets?.[0]) || 0 },
        ]

        let index = 0
        zoneMappings.forEach(({ zone, amount }) => {
          index = addChipsForZone(zone, amount, index)
        })
      }
    },

    /**
     * 清除临时下注 (时间到了,还未点击确认)
     */
    clearPendingBets(tableId: string) {
      const normalizedId = normalizeUUID(tableId)
      if (this.tableBets[normalizedId]) {
        this.tableBets[normalizedId].pendingBets = []
      }
    },

    /**
     * 移除指定的下注 (下注失败时调用)
     */
    removeBet(tableId: string, betId: string) {
      const normalizedId = normalizeUUID(tableId)
      if (this.tableBets[normalizedId]) {
        const betData = this.tableBets[normalizedId]
        betData.confirmedBets = betData.confirmedBets.filter(bet => bet.betId !== betId)
      }
    },

    /**
     * 清空所有下注 (新局开始时调用)
     */
    clearAllBets(tableId: string) {
      const normalizedId = normalizeUUID(tableId)

      // 先初始化 (如果不存在)
      this.initTableBetData(normalizedId)

      // 清空所有下注 (initTableBetData 保证了 tableBets[normalizedId] 存在)
      const betData = this.tableBets[normalizedId]
      if (betData) {
        // 使用 splice 清空数组以保持响应式
        betData.pendingBets.splice(0, betData.pendingBets.length)
        betData.confirmedBets.splice(0, betData.confirmedBets.length)
      }
    },

    /**
     * 获取台桌的临时下注金额 (仅 pendingBets)
     * 用于余额验证，因为已确认下注的金额已从余额中扣除
     */
    getPendingBetAmount(tableId: string): number {
      const normalizedId = normalizeUUID(tableId)
      if (!this.tableBets[normalizedId]) return 0

      const betData = this.tableBets[normalizedId]
      return betData.pendingBets.reduce((sum, bet) => sum + bet.amount, 0)
    },

    /**
     * 获取台桌的总下注金额 (临时 + 已确认)
     */
    getTotalBetAmount(tableId: string): number {
      const normalizedId = normalizeUUID(tableId)
      if (!this.tableBets[normalizedId]) return 0

      const betData = this.tableBets[normalizedId]
      const pendingTotal = betData.pendingBets.reduce((sum, bet) => sum + bet.amount, 0)
      const confirmedTotal = betData.confirmedBets.reduce((sum, bet) => sum + bet.amount, 0)

      return pendingTotal + confirmedTotal
    },

    /**
     * 获取指定区域的下注金额 (临时 + 已确认)
     */
    getZoneBetAmount(tableId: string, zone: string): number {
      const normalizedId = normalizeUUID(tableId)
      if (!this.tableBets[normalizedId]) return 0

      const betData = this.tableBets[normalizedId]
      const pendingAmount = betData.pendingBets
        .filter(bet => bet.zone === zone)
        .reduce((sum, bet) => sum + bet.amount, 0)
      const confirmedAmount = betData.confirmedBets
        .filter(bet => bet.zone === zone)
        .reduce((sum, bet) => sum + bet.amount, 0)

      return pendingAmount + confirmedAmount
    },
  },
})
